import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import { ClockIcon, CheckmarkIcon, ProgressCircleIcon, ExclamationIcon } from '../../icons/app/';
import { Text, Segmented, Button } from '../../../components';
import { RMCard } from '../RMCard';
import { LoadableValue, useCardLoadingState, SKELETON_WIDTH } from '../CardLoadingUtils';
import { ROUTE_CONFIG } from '../../../constants/routes';
import { formatCount } from '../../../lib/helpers';
import './MigrationStatusCard.scss';

const PERIODS = ['Day', 'Week', 'Month'];
const DEFAULT_PERIOD = PERIODS[0];

export const MigrationStatusCard = ({ migrationData = {} }) => {
    const [period, setPeriod] = useState(DEFAULT_PERIOD);

    const handlePeriodChange = (newPeriod) => {
        setPeriod(newPeriod);
        migrationData.fetchMigrationStatusForPeriod?.(newPeriod.toLowerCase());
    };

    const { count: inProgress = 0 } = migrationData.inProgress || {};
    const { scheduled = 0, completed = 0, failed = 0 } = migrationData.current || {};
    const { loading, isError: currentError, disabled: currentDisabled } = useCardLoadingState(migrationData.current);
    const {
        loading: activeLoading,
        isError: activeError,
        disabled: activeDisabled,
    } = useCardLoadingState(migrationData.inProgress);
    const isError = currentError || activeError;
    const disabled = currentDisabled || activeDisabled;

    const statusRows = [
        { icon: <ClockIcon />, label: 'Scheduled', count: formatCount(scheduled) },
        {
            icon: <CheckmarkIcon />,
            label: 'Completed',
            count: formatCount(completed),
        },
        {
            icon: <ExclamationIcon />,
            label: 'Failed',
            count: formatCount(failed),
            countClassName: 'failed-count',
        },
    ];

    return (
        <RMCard className="migration-status-card" showMoreIcon={false} isError={isError}>
            <header>
                <div className="header-left">
                    <Text size="large" color="secondary">
                        Migration Status
                    </Text>
                </div>
                <Segmented
                    options={PERIODS}
                    value={period}
                    onChange={handlePeriodChange}
                    size="small"
                    shape="round"
                    disabled={disabled}
                />
            </header>
            <div className="migration-status-body">
                <div className="migration-status-left">
                    <div className="in-progress-section">
                        <span className="in-progress-label">
                            <ProgressCircleIcon className="in-progress-icon" />
                            <Text size="small" color="muted" bold>
                                In Progress
                            </Text>
                        </span>
                        <LoadableValue
                            isLoading={activeLoading}
                            width={SKELETON_WIDTH.large}
                            className="in-progress-count"
                        >
                            <Text size="extraLarge" xxlBold color="primaryBlue" className="in-progress-count">
                                {formatCount(inProgress)}
                            </Text>
                        </LoadableValue>
                    </div>
                    <Link to={ROUTE_CONFIG.migrationList.path}>
                        <Button type="primary" disabled={disabled}>
                            View Migrations <RightOutlined />
                        </Button>
                    </Link>
                </div>
                <div className="migration-status-right">
                    {statusRows.map(({ icon, label, count, countClassName = 'status-count' }) => (
                        <div className="status-row" key={label}>
                            <div className="status-header">
                                {icon}
                                <Text size="small" color="muted" bold>
                                    {label}
                                </Text>
                            </div>
                            <LoadableValue isLoading={loading} width={SKELETON_WIDTH.medium}>
                                <Text medLarge xxlBold className={countClassName}>
                                    {count}
                                </Text>
                            </LoadableValue>
                        </div>
                    ))}
                </div>
            </div>
        </RMCard>
    );
};
